import { useState } from 'react';
import {useNavigate} from 'react-router-dom';
import {API_BASE_URL} from '../../../config';
import {useAuthContext} from '../../../context/authContext';
import {Box, IconButton, InputAdornment, Typography} from "@mui/material";
import {
  AuthAboutButton, AuthAnswer,
  AuthContainer,
  AuthField, AuthFields, AuthFooter,
  AuthHeader,
  AuthLabel, AuthLinkButton,
  AuthSubTitle,
  AuthTitle,
  AuthWrapper
} from "../auth.styled";

import ImageBg from '../../../assets/img/bgs/authBg.png'
import {UiGradientInput} from "../../../components/ui/includes/GradientInput";
import {OpenEye} from "../../../assets/svg/auth/openEye";
import {CloseEye} from "../../../assets/svg/auth/closeEye";
import {Field, Form, Formik} from 'formik'
import {SingInSchema} from "./model/signIn.schema";

const initialValues = {
  email: '',
  password: '',
}

export const SignIn = () => {
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const {setAccessToken} = useAuthContext();
  const navigate = useNavigate();


  const handleSubmit = async (values: { email: string; password: string; }) => {

    try {
      const response = await fetch(API_BASE_URL + '/auth/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auth_provider_id: values.email,
          auth_provider_token: values.password,
        }),
      });

      if (!response.ok) {
        console.error(`Login failed with status: ${response.status}`);
        const errorResponse = await response.json();
        console.error('Error details:', errorResponse);

        setError('Invalid login credentials. Please try again.');
        return;
      }

      const body = (await response.json()) as ApiResponse<AuthData>;
      if (!body.success || !body.data || body.errors) {
        setError('Invalid response from the server. Please try again.');
        return;
      }

      const {access_token, refresh_token} = body.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setAccessToken(access_token);
      navigate('/dashboard');
    } catch (error) {
      console.error('An error occurred during login:', error);
      setError('An unexpected error occurred. Please try again later.');
    }
  };

  const clickShowPassword = () => setShowPassword(show => !show)

  return (
    <AuthWrapper>
      <img
        src={ImageBg}
        style={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          maxWidth: '100%',
          minWidth: '617px',
          display: 'block',
        }}
        alt='bg'/>
      <AuthContainer>
        <AuthHeader>
          <AuthTitle variant="h1">Edge Drive</AuthTitle>
          <AuthSubTitle variant="h1">Sign In</AuthSubTitle>
        </AuthHeader>
        <Box>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={(values) => handleSubmit(values)}
            validationSchema={SingInSchema}
          >
            {(formik) => {
              const { values, errors, touched, isValid, dirty } = formik;

              return (
                <Form>
                  <AuthFields>
                    <AuthField>
                      <AuthLabel>Email</AuthLabel>
                      <Field
                        as={UiGradientInput}
                        name="email"
                        placeholder={'mail@mail.com'}
                        value={values.email}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </AuthField>
                    <AuthField mb={error ? '16px' : '32px'}>
                      <AuthLabel>Password</AuthLabel>
                      <Field
                        as={UiGradientInput}
                        name="password"
                        placeholder={'******'}
                        value={values.password}
                        type={showPassword ? 'text' : 'password'}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{mr: '6px'}}>
                              <IconButton
                                aria-label="close"
                                onClick={clickShowPassword}
                                edge="end"
                              >
                                {showPassword ? <OpenEye/> : <CloseEye/>}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </AuthField>
                    {error && <Typography fontSize={'12px'} mb={'16px'} color={'red'}>Invalid email or password</Typography>}
                    <AuthAboutButton
                      type="submit"
                      disabled={!(isValid && dirty)}
                      variant={'contained'}
                    >Sign in</AuthAboutButton>
                  </AuthFields>
                </Form>
              )
            }}
          </Formik>

          <AuthFooter>
            <AuthAnswer>Don’t have an account?</AuthAnswer>
            <AuthLinkButton variant={'text'} href={`${process.env.PUBLIC_URL}/signup`}>Register</AuthLinkButton>
          </AuthFooter>
        </Box>
      </AuthContainer>
    </AuthWrapper>
  );
};
